import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { RootActionType, RootState } from 'duck'
import { actions, FIRST_STEPS, selectors, STEP } from '../duck'
import { ReduxProps } from 'utils/Types'
import Typography from 'components/Typography'
import Button from 'components/Button'
import Stepper from 'components/Stepper'
import { useBreakpoint } from 'utils/Hooks'
import FormSellNft from './FormSellNft'
import CurateCollection from './CurateCollection'
import FormSubmit from './FormSubmit'
import { dialogActions, dialogSelectors, FormDialog } from 'duck/AppDuck/DialogDuck'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './NFTSellForm.module.scss'
import { appActions } from 'duck/AppDuck'

export type SubviewProps = 'info' | 'list'
const TEXT_STEP = ['CURATE COLLECTION', 'DETAILS', 'SUBMIT']

const DIALOG_CLASSES = {
  paper: styles.DialogFormSellNft
}

const mapStateToProps = (state: RootState) => ({
  isSelectedImageEnough: selectors.isSelectedImageEnough(state),
  gridLoading: selectors.gridLoading(state),
  activeDialog: dialogSelectors.activeDialog(state),
  dialogData: dialogSelectors.dialogDatum[FormDialog.SELL_AS_NFT_FORM](state),
  step: selectors.step(state),
  maxStep: selectors.maxStep(state),
  isResubmitForm: selectors.isResubmitForm(state),
  thumbnail: selectors.formData(state).thumbnail
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      disconnect: appActions.metamask.disconnect,
      closeDialog: dialogActions.closeDialog,
      initForm: actions.initForm,
      loadAllImageGrid: actions.loadAllImageGrid,
      setStep: actions.setStep,
      resetSelection: actions.imageSelect.resetSelection
    },
    dispatch
  )

type DialogFormSellNftProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps>

const NFTSellForm: React.FC<DialogFormSellNftProps> = props => {
  const {
    gridLoading,
    isSelectedImageEnough,
    step,
    maxStep,
    activeDialog,
    dialogData,
    thumbnail,
    isResubmitForm,
    disconnect,
    resetSelection,
    loadAllImageGrid,
    setStep,
    initForm,
    closeDialog
  } = props

  const showDialog = activeDialog === FormDialog.SELL_AS_NFT_FORM

  const { isMediumscreenAbove, isSmallscreenAbove } = useBreakpoint()
  const dialogBodyRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    disconnect()
    closeDialog()
  }, [closeDialog, disconnect])

  const stepItems = useMemo(
    () =>
      TEXT_STEP.map((title, index) => {
        const stepAdjusted = index === 0 ? index + 1 : index + 2

        const onClick =
          stepAdjusted <= maxStep
            ? () => {
                setStep(stepAdjusted)
              }
            : undefined

        return {
          title,
          onClick: isResubmitForm && index === 0 ? undefined : onClick
        }
      }),
    [isResubmitForm, maxStep, setStep]
  )

  const id = dialogData?.content?.id
  const artMineProjectId = dialogData?.content?.artMineProjectId
  const projectType = dialogData?.content?.projectType

  useEffect(() => {
    const projectId = id || artMineProjectId

    if (showDialog && projectId) {
      initForm({ id: projectId, projectType: projectType || 'artmineProject' })
      loadAllImageGrid({ reload: true })
    }
    if (!showDialog) {
      resetSelection()
    }
  }, [initForm, resetSelection, loadAllImageGrid, showDialog, id, projectType, artMineProjectId])

  return (
    <Dialog
      open={showDialog}
      classes={DIALOG_CLASSES}
      onClose={handleClose}
      maxWidth={false}
      // BackdropProps={{
      //   className: styles.NFTSellFormBackdrop
      // }}
      fullScreen
    >
      {isMediumscreenAbove ? (
        <div className={clsx('absolute right-0 top-0 z-1', styles.ButtonDialogCloseWrapper)}>
          <Button className="!p-0 sm:text-5xl" aria-label="Close" onClick={handleClose} icon>
            <CloseIcon fontSize="inherit" />
          </Button>
        </div>
      ) : null}

      <div className={clsx('h-full overflow-hidden', styles.NFTSellFormContent)}>
        <div className="flex h-full flex-col bg-primary">
          <div className={clsx(stylesGlobal.BorderBottomOnSurface, styles.Container)}>
            <div
              className={clsx(
                'flex items-center',
                !isMediumscreenAbove && 'justify-between',
                styles.NFTSellFormHeadTop
              )}
            >
              {/* If in Curate Info Step  */}
              {step === STEP.CURATE_COLLECTION || step === STEP.SELECT_COVER ? (
                <Button
                  aria-label="Close"
                  size="small"
                  onClick={() => {
                    setStep(step - 1)
                  }}
                  startIcon={<ChevronLeftIcon />}
                >
                  BACK
                </Button>
              ) : (
                <Typography variant={isSmallscreenAbove ? 'h5' : 'h6'}>Sell as NFT</Typography>
              )}

              {!isMediumscreenAbove ? (
                <Button className="!p-0 sm:text-5xl" aria-label="Close" onClick={handleClose} icon>
                  <CloseIcon fontSize="inherit" />
                </Button>
              ) : null}
            </div>
          </div>

          <div
            ref={dialogBodyRef}
            className="relative flex h-1/100 min-h-0 flex-auto flex-col overflow-auto"
          >
            <div className={clsx('flex justify-center pt-8', isSmallscreenAbove ? 'pb-5' : 'pb-9')}>
              <Stepper data={stepItems} activeIndex={step - 2 < 0 ? 0 : step - 2} />
            </div>

            {showDialog && FIRST_STEPS.includes(step) ? (
              <CurateCollection dialogBodyRef={dialogBodyRef} />
            ) : null}
            {showDialog && step === STEP.DETAILS ? <FormSellNft scrollRef={dialogBodyRef} /> : null}
            {showDialog && step === STEP.SUBMIT ? (
              <div className="h-1/100 min-h-0 flex-auto pb-20 lg:pb-30">
                <FormSubmit />
              </div>
            ) : null}
          </div>

          {!isSmallscreenAbove &&
          (step === STEP.SELECT_COVER || step === STEP.CURATE_COLLECTION) ? (
            <div>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => {
                  setStep(STEP.DETAILS)
                }}
                disabled={
                  !Boolean(thumbnail) ||
                  !isSelectedImageEnough ||
                  Boolean(gridLoading) ||
                  step === STEP.CURATE_COLLECTION
                }
                fullWidth
              >
                {gridLoading ? `Loading Image...` : `CONTINUE`}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(NFTSellForm)
